import { formatCents, parseMoneyToCents, roundToCashStep, tt } from '@agos/shared'

/**
 * The discount fields in `/restaurant`'s payment dialog.
 *
 * A waiter closing a table is where a discount is actually given — the customer is standing there, the
 * bill is on the screen, and *"dáme vám desať percent"* happens at that moment and not when the order was
 * taken. Legacy has manual (percent or fixed) and a coupon, both applied to what is still due; the port
 * had neither.
 *
 * **The two roundings are not the same, and legacy's restaurant dialog gets this right where the
 * customer checkout does not.** `Money` draws the line: `cents()` — *"an amount that is arithmetic rather
 * than cash: a line price, a subtotal, a discount"* — and `payable()`, *"what the customer actually pays,
 * rounded so it can be handed over in coins"*. So a discount keeps its cent and only the total takes the
 * five-cent step. That is what happens here; FB-C04 records the customer app doing it the other way.
 *
 * **A discount cannot exceed the bill.** `Money.notNegative` exists for the same reason on the server —
 * *"a discount larger than the bill does not mean the restaurant owes money"* — and the cap is applied
 * per source and again on the sum, because two sources that each fit can together overshoot.
 */

export type DiscountMode = 'percent' | 'fixed'

export interface PayDiscount {
  mode: DiscountMode
  value: string
  couponCode: string
  couponCents: number
  couponError: string | null
}

export const NO_DISCOUNT: PayDiscount = {
  mode: 'percent',
  value: '',
  couponCode: '',
  couponCents: 0,
  couponError: null,
}

const AMOUNT = /^\d+(?:[.,]\d{1,2})?$/

/** The typed discount in cents, or `null` when it is not a number at all. */
export function manualCents(discount: PayDiscount, dueCents: number): number | null {
  const raw = discount.value.trim()
  if (raw === '') return 0
  if (!AMOUNT.test(raw)) return null
  if (discount.mode === 'percent') {
    const percent = Number(raw.replace(',', '.'))
    if (!Number.isFinite(percent)) return null
    // To the cent, not to the coin — a discount is arithmetic.
    return Math.min(dueCents, Math.round((dueCents * percent) / 100))
  }
  return Math.min(dueCents, parseMoneyToCents(raw))
}

/** Everything coming off the bill, capped at the bill. */
export function totalDiscountCents(discount: PayDiscount, dueCents: number): number {
  const manual = manualCents(discount, dueCents) ?? 0
  return Math.min(dueCents, manual + Math.max(0, discount.couponCents))
}

/** What the customer hands over — the only figure that takes the five-cent step. */
export function payableCents(discount: PayDiscount, dueCents: number): number {
  return roundToCashStep(Math.max(0, dueCents - totalDiscountCents(discount, dueCents)))
}

/** What is wrong with the fields, said before the button rather than by the server afterwards. */
export function discountProblems(discount: PayDiscount, dueCents: number): string[] {
  const found: string[] = []
  if (manualCents(discount, dueCents) === null) {
    found.push(tt('Zľava nie je číslo.'))
  } else if (discount.mode === 'percent') {
    const percent = Number(discount.value.trim().replace(',', '.'))
    if (discount.value.trim() !== '' && percent > 100) {
      found.push(tt('Percento nemôže byť viac ako 100.'))
    }
  }
  return found
}

/**
 * The line under the fields.
 *
 * Names the discount and what is left, and says when the rounding moved the figure — a bill that reads
 * 12,33 € and is charged 12,35 € should say so at the counter rather than on the receipt.
 */
export function discountNote(discount: PayDiscount, dueCents: number): string {
  const off = totalDiscountCents(discount, dueCents)
  if (off === 0) return `${tt('Na zaplatenie')} ${formatCents(payableCents(discount, dueCents))}`
  const exact = Math.max(0, dueCents - off)
  const payable = payableCents(discount, dueCents)
  const rounded =
    payable === exact ? '' : ` (${tt('zaokrúhlené na 5 centov z')} ${formatCents(exact)})`
  return `${tt('Zľava')} ${formatCents(off)} - ${tt('na zaplatenie')} ${formatCents(payable)}${rounded}`
}

/** The body `POST /coupons/validate` takes, in euros as the endpoint reads it. */
export function couponPayload(
  discount: PayDiscount,
  dueCents: number,
): { code: string; orderAmount: number } {
  return { code: discount.couponCode.trim().toUpperCase(), orderAmount: dueCents / 100 }
}

/*
 * `discountForClose` was here until §9.770 - the typed zľava alone, in euros, and its one caller was the receipt. That
 * is FB-1088: an order already carrying a zľava and given a further one registered the lines less the further one
 * alone. The receipt now prints the zľava the order holds (`recordedDiscountCents` in `payment-in-flight.ts`, or the
 * server's own portion on a split), so the function went with its last caller rather than stay as a second answer.
 */

/**
 * The discount as `POST /orders/split-payment` takes it - §9.394, FB-699.
 *
 * <p><b>This is the wire that was missing, and the javadoc of the receipt's helper already claimed it.</b>
 * `discountForClose` was called for the fiscal receipt and nowhere else, so the register printed a
 * document for the discounted total and the order closed at the full one: the tax document and the
 * order disagreed by the discount, and the day's takings were high by it.
 *
 * <p><b>Absent rather than zero when nothing was given.</b> The server's `manualDiscountOn` treats a
 * null or non-positive amount as nothing, so either would compute the same - but a `discountType` on
 * an order with no discount would be written into `manual_discount_type` and relabel an order that
 * carried a **coupon** as a manual discount (§10A.172 keeps the provenance behind the same flag).
 *
 * <p><b>`fixed` unless the manual field is a percentage.</b> The endpoint has one pair of fields and
 * the coupon's cents are summed into the same amount - §9.394 says why - so the type describes the
 * figure actually being sent. A percentage typed **with** a coupon beside it is `fixed`, because the
 * sum is a sum of cents and calling it a percentage would make the server recompute it from the
 * bill.
 */
export function closeDiscountPayload(
  discount: PayDiscount,
  dueCents: number,
): { discountType: DiscountMode; discountAmount: number } | Record<string, never> {
  const total = totalDiscountCents(discount, dueCents)
  if (total <= 0) return {}
  const manual = manualCents(discount, dueCents) ?? 0
  const coupon = Math.max(0, discount.couponCents)
  const percentOnly = discount.mode === 'percent' && manual > 0 && coupon === 0
  return {
    discountType: percentOnly ? 'percent' : 'fixed',
    // Percent sends the figure that was typed; fixed sends euros, as the endpoint reads them.
    discountAmount: percentOnly ? Number(discount.value.trim().replace(',', '.')) : total / 100,
  }
}

/*
 * `closeMetaDiscount` was here until §9.687 - it renamed {@link closeDiscountPayload}'s two keys for
 * `AuthActionMeta`, because the close without a document went through `EmployeeAuthModal`. Owner #116
 * removed that prompt, so the close builds its own body (`closeWithoutDocumentBody`) and takes this
 * function's answer **unrenamed** - still one function for the zľava on both closes, which is what §10A.216
 * asked for. Removed rather than kept, because a function whose only caller is its own test is not one.
 */
